import type { ApiQuery, ApiRequestBody, ApiResponse } from "@takt/api-contracts"
import type { ApiClient } from "../client"
import type { PaginatedQuery } from "./query-types"

type ResourceId = string | number
const id = (value: ResourceId) => encodeURIComponent(String(value))

export type ListOwnedCompaniesQuery = PaginatedQuery<NonNullable<ApiQuery<"/v1/companies/mine", "get">>>
export type ListPendingCompaniesQuery = PaginatedQuery<NonNullable<ApiQuery<"/v1/companies/pending", "get">>>
export type ListApprovedCompaniesQuery = PaginatedQuery<NonNullable<ApiQuery<"/v1/companies/approved", "get">>>
export type CompaniesResponse = ApiResponse<"/v1/companies/approved", "get", 200>
export type PublicCompanyResponse = ApiResponse<"/v1/companies/public/{slug}", "get", 200>
export type CompanyResponse = ApiResponse<"/v1/companies/{companyId}", "get", 200>
export type CreateCompanyInput = ApiRequestBody<"/v1/companies", "post">
export type UpdateCompanyInput = ApiRequestBody<"/v1/companies/{companyId}", "patch">
export type CompanyReadinessResponse = ApiResponse<"/v1/companies/{companyId}/readiness", "get", 200>
export type CompanySettingsResponse = ApiResponse<"/v1/companies/{companyId}/settings", "get", 200>
export type UpdateCompanySettingsInput = ApiRequestBody<"/v1/companies/{companyId}/settings", "patch">
export type CompanyCategoryCatalog = ApiResponse<"/v1/company-categories", "get", 200>
export type CompanyCategory = CompanyCategoryCatalog[number]
export type ReplaceCompanyCategoriesInput = ApiRequestBody<"/v1/companies/{companyId}/categories", "put">

export function createCompanyEndpoints(client: ApiClient) {
    return {
        listCompanyCategories(signal?: AbortSignal) {
            return client.get<CompanyCategoryCatalog>("/company-categories", { signal })
        },
        listOwnedCompanies(query: ListOwnedCompaniesQuery = {}) {
            return client.get<ApiResponse<"/v1/companies/mine", "get", 200>>("/companies/mine", { query })
        },
        listPendingCompanies(query: ListPendingCompaniesQuery = {}) {
            return client.get<ApiResponse<"/v1/companies/pending", "get", 200>>("/companies/pending", { query })
        },
        listApprovedCompanies(query: ListApprovedCompaniesQuery = {}) {
            return client.get<CompaniesResponse>("/companies/approved", { query })
        },
        getPublicCompanyBySlug(slug: string, signal?: AbortSignal) {
            return client.get<PublicCompanyResponse>(`/companies/public/${encodeURIComponent(slug)}`, { signal })
        },
        getCompany(companyId: ResourceId, signal?: AbortSignal) {
            return client.get<CompanyResponse>(`/companies/${id(companyId)}`, { signal })
        },
        createCompany(input: CreateCompanyInput) {
            return client.post<ApiResponse<"/v1/companies", "post", 201>>("/companies", input)
        },
        updateCompany(companyId: ResourceId, input: UpdateCompanyInput) {
            return client.patch<ApiResponse<"/v1/companies/{companyId}", "patch", 200>>(
                `/companies/${id(companyId)}`,
                input,
            )
        },
        replaceCompanyCategories(companyId: ResourceId, input: ReplaceCompanyCategoriesInput) {
            return client.put<ApiResponse<"/v1/companies/{companyId}/categories", "put", 200>>(
                `/companies/${id(companyId)}/categories`,
                input,
            )
        },
        approveCompany(companyId: ResourceId) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/approve", "post", 200>>(
                `/companies/${id(companyId)}/approve`,
            )
        },
        rejectCompany(companyId: ResourceId) {
            return client.post<ApiResponse<"/v1/companies/{companyId}/reject", "post", 200>>(
                `/companies/${id(companyId)}/reject`,
            )
        },
        deleteCompany(companyId: ResourceId) {
            return client.delete<null>(`/companies/${id(companyId)}`)
        },
        getCompanyReadiness(companyId: ResourceId) {
            return client.get<CompanyReadinessResponse>(`/companies/${id(companyId)}/readiness`)
        },
        getCompanySettings(companyId: ResourceId) {
            return client.get<CompanySettingsResponse>(`/companies/${id(companyId)}/settings`)
        },
        updateCompanySettings(companyId: ResourceId, input: UpdateCompanySettingsInput) {
            return client.patch<ApiResponse<"/v1/companies/{companyId}/settings", "patch", 200>>(
                `/companies/${id(companyId)}/settings`,
                input,
            )
        },
    }
}
